<template>
    <div class="my-2 relative">
        <div class="relative">
            <slot></slot>

            <div v-if="inputUse === 'search'">
                <div :class="status ? 'error' : ''" class="input-wrapper relative rounded-2xl">
                    <input
                        v-maska="mask"
                        :type="type"
                        :name="name"
                        :value="value"
                        :required="required"
                        :status="status"
                        :autocomplete="autocomplete"
                        placeholder=" "
                        :aria-labelledby="name"
                        class="input block p-3 w-full appearance-none focus:outline-none bg-transparent"
                        :class="inputClass"
                        @input="[$emit('update:value', $event.target.value), detectInputStrength($event)]"
                        @paste="onPaste($event)"
                    />

                    <label
                        v-if="label"
                        :for="name"
                        class="input-label pointer-events-none absolute top-0 text-blue-dark/50 p-3 duration-300 origin-0 rounded-md"
                        :class="[required ? 'after:content-[' * '] after:ml-0.5 after:text-red' : '', labelClass]"
                    >
                        {{ label }}
                    </label>

                    <label v-if="label" :for="name" class="input-label-icon pointer-events-none absolute top-0 right-2 text-blue-dark/50 p-3 duration-300 origin-0 rounded-md">
                        <Icon type="Search" viewBox="0 0 19 18" width="19" height="18" class="fill-current" />
                    </label>
                </div>
            </div>

            <template v-else>
                <div :class="status ? 'error' : ''" class="input-wrapper relative rounded-md shadow-md bg-blue-light/50">
                    <input
                        v-maska="mask"
                        :type="type"
                        :name="name"
                        :value="value"
                        :required="required"
                        :status="status"
                        :autocomplete="autocomplete"
                        placeholder=" "
                        :aria-labelledby="name"
                        class="input block p-3 w-full appearance-none focus:outline-none bg-transparent"
                        :class="inputClass"
                        @input="[$emit('update:value', $event.target.value), detectInputStrength($event)]"
                        @paste="onPaste($event)"
                    />

                    <label
                        v-if="label"
                        :for="name"
                        class="input-label pointer-events-none absolute top-0 text-blue-dark/50 p-3 duration-300 origin-0 rounded-md"
                        :class="[required ? 'after:content-[' * '] after:ml-0.5 after:text-red' : '', labelClass]"
                    >
                        {{ label }}
                    </label>

                    <div v-if="strengthMeter && value" class="w-full absolute bottom-0">
                        <div v-if="passwordCheck">
                            <div v-if="passwordCheck != value">
                                <div :style="{ 'background-color': '#cc3300' }" class="w-full h-1 relative rounded-md shadow-md"></div>
                            </div>
                        </div>
                        <div v-else :style="{ 'background-color': inputStrengthColor }" class="w-full h-1 relative rounded-md shadow-md"></div>
                    </div>
                </div>
                <div v-if="strengthMeter && value">
                    <div v-if="passwordCheck">
                        <small v-if="passwordCheck != value">
                            {{ $t('account_password_not_equal') }}
                        </small>
                    </div>
                    <small v-else> {{ $t('account_password_is') }} {{ strengthMeterNotice[inputStrength] }} </small>
                </div>
            </template>
        </div>
        <FormError v-model:status="status" :form-component-id="formComponentId" :name="name" />
    </div>
</template>

<script>
import i18next from 'i18next';
import Icon from '../miscellaneous/Icon.vue';

export default {
    components: { Icon },
    props: {
        value: String,
        type: String,
        required: Boolean,
        name: String,
        label: String,
        autocomplete: String,
        preventinsert: Boolean,
        strengthMeter: Boolean,
        passwordCheck: String,
        mask: String,
        formComponentId: '',

        containerClass: String,
        inputClass: String,
        labelClass: String,
        inputUse: String
    },

    emits: ['update:value'],

    data: () => ({
        status: false,
        inputStrength: null,
        inputStrengthColor: null
    }),

    computed: {
        strengthMeterNotice() {
            return {
                0: i18next.t('plain_weak'),
                1: i18next.t('plain_weak'),
                2: i18next.t('plain_weak'),
                3: i18next.t('plain_medium'),
                4: i18next.t('plain_medium'),
                5: i18next.t('plain_medium'),
                6: i18next.t('plain_medium'),
                7: i18next.t('plain_strong'),
                8: i18next.t('plain_strong'),
                9: i18next.t('plain_strong'),
                10: i18next.t('plain_strong')
            };
        }
    },

    mounted() {
        this.$nextTick(() => {
            if (this.value) {
                this.detectInputStrength(this.value);
            }
        });
    },

    methods: {
        onPaste(event) {
            if (this.preventinsert) {
                event.preventDefault();

                return false;
            }
        },
        detectInputStrength(event) {
            let strength = 0;

            // use event target value or the direct value if its not an object
            const value = event?.target?.value || event;

            new RegExp('(?=.{6,})').test(value) // Length of 6
                ? (strength += 0)
                : (strength += 0);

            new RegExp('(?=.{8,})').test(value) // Length of 8
                ? (strength += 2)
                : (strength += 0);

            new RegExp('(?=.{10,})').test(value) // Length of 10
                ? (strength += 2)
                : (strength += 0);

            new RegExp('(?=.*[A-Z])').test(value) // at least one uppercase letter
                ? (strength += 1)
                : (strength += 0);

            new RegExp('(?=.*[a-z])').test(value) // at least one lowercase letter
                ? (strength += 1)
                : (strength += 0);

            new RegExp('(?=.*[0-9])').test(value) // at least one digit
                ? (strength += 1)
                : (strength += 0);

            new RegExp('([^A-Za-z0-9])').test(value) // at least one special character
                ? (strength += 1)
                : (strength += 0);

            this.inputStrength = strength;

            const g = 255 * (strength / 10);
            const r = 255 - 255 * (strength / 10);
            this.inputStrengthColor = 'rgb(' + r + ',' + g + ',0)';
        }
    }
};
</script>
